import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import YoyTrend, { MEASURES } from './YoyTrend';
import YoyBreakdown from './YoyBreakdown';
import YoySourceData from './YoySourceData';
import { api } from '../../services/api';
import { c, t } from '../../theme';

// ── Year over year, on the board ─────────────────────────────────────────────
//
// This lived on its own page reached from a link in the board's subtitle, and
// a link is where a panel goes to be forgotten. It is the question the monthly
// operations review opens with, so it belongs where the review is already
// looking.
//
// IT INHERITS THE BOARD'S FILTERS RATHER THAN CARRYING ITS OWN. The board
// already has a lens, a group and a labour category above it, and a second set
// of the same controls inside a panel is how two things on one screen come to
// disagree. `filterQ` is the board's filter state; everything here is that cut.
// What it does own is what only it needs: the measure, how far back, and which
// dimension the table breaks out.
//
// THE GRAIN IS MONTHLY WHATEVER LEVEL THE BOARD IS ON. A year-over-year
// comparison of a single pay period against "the same week last year" is not a
// thing -- weeks do not line up across years, which is the whole reason a month
// is defined here as the runs that ended in it. So this panel does not follow
// the board's L0/L1 grain, and says so rather than appearing to ignore it.

// OT as a percent of hours worked is deliberately absent. The payroll extract
// carries premium codes ONLY -- overtime, double time, shift differentials,
// missed meals -- and has never received a straight-time row (see the
// calculation dictionary). So the denominator that measure needs does not exist
// in this feed: it divides overtime by overtime and reads ~98% every month.
// Per-run is what actually neutralises the four-against-five artifact, and it
// is in the table below, computed from figures that do exist.
const MEASURE_TABS = [
  { key: 'hours',  label: 'OT hours' },
  { key: 'amount', label: 'OT cost' },
];

// Six and twelve only. Twenty-four and thirty-six drew a comparison chart whose
// older half had nothing to compare against: the payroll we hold begins in
// Dec '25, so at 24 months every slot left of it rendered as a lone bar with no
// prior year beside it, and at 36 most of the chart was that. A reader scanning
// bar heights across those slots is reading this year against nothing, which is
// the one thing a year-over-year panel must not invite. Twelve is one of each
// month, which is the comparison itself; six is the recent view.
const WINDOWS = [
  { key: '6',  label: '6 mo'  },
  { key: '12', label: '12 mo' },
];

export default function YoyPanel({ filterQ = '', canViewPeople = true }) {
  const [measure, setMeasure] = useState('hours');
  // Twelve by default: the comparison is month against the same month a year
  // earlier, so a year of them is one of each and is what the monthly review
  // reads. Six narrows to the recent picture; nothing longer is offered, for
  // the reason given on WINDOWS above.
  const [months,  setMonths]  = useState('12');
  const [by,      setBy]      = useState('family');
  const [month,   setMonth]   = useState('');
  const [cc,      setCc]      = useState('');

  const ccQ = cc ? `&cc=${encodeURIComponent(cc)}` : '';

  const trend = useQuery({
    queryKey: ['ot-yoy-trend', months, filterQ, cc],
    queryFn:  () => api.get(`/overtime/compare/trend?months=${months}${filterQ}${ccQ}`),
  });

  // What may be grouped by is the API's decision, not the panel's: manager is
  // person-adjacent and the server withholds it from read-only roles. Asking
  // keeps one gate rather than two that can drift apart.
  const dims = useQuery({
    queryKey: ['ot-compare-dims', canViewPeople],
    queryFn:  () => api.get('/overtime/compare/dimensions'),
  });

  const points = trend.data?.points || [];
  const anchor = trend.data?.anchor || '';
  const cov    = trend.data?.coverage;

  // Default the table to the newest CLOSED month rather than the newest month.
  // The newest is usually part-loaded, and opening a breakdown on a fifth of a
  // month invites every row to be read as a collapse.
  const closed = points.filter((p) => p.loaded && !p.partial);
  const defaultMonth = closed.length ? closed[closed.length - 1].month : anchor;
  const activeMonth  = month || defaultMonth;

  // What the server will accept RIGHT NOW. `requires_cost_center` is the
  // server's own flag on the dimension, so the picker gates on the same rule
  // /compare/by enforces rather than a second copy of it that can drift.
  const allDims       = dims.data?.dimensions || [];
  const availableDims = allDims.filter((d) => !d.requires_cost_center || cc);

  // Derived, not reset in an effect. Clearing the cost center while broken down
  // by employee has to stop asking for employee on THIS render; an effect fires
  // after one, and that one render is a request the server answers with a 400.
  // The chosen dimension is kept in state rather than overwritten, so picking
  // the cost center again brings it back.
  const effectiveBy = availableDims.some((d) => d.key === by)
    ? by
    : (availableDims[0]?.key || 'family');

  const breakdown = useQuery({
    queryKey: ['ot-yoy-by', effectiveBy, activeMonth, filterQ, cc],
    queryFn:  () => api.get(`/overtime/compare/by?by=${effectiveBy}&month=${activeMonth}${filterQ}${ccQ}`),
    enabled:  !!activeMonth,
  });

  // Named on screen whenever it is set: a chart showing one yard under a
  // heading that says "Overtime" is how a yard's number gets read as the
  // company's.
  const ccRow   = (dims.data?.cost_centers || []).find((x) => x.id === cc);
  const ccLabel = ccRow ? `${ccRow.code} · ${ccRow.name}` : null;

  const comparable = points.filter((p) => p.prior.loaded).length;
  const noneYet    = cov && comparable === 0 && points.length > 0;

  return (
    <section style={s.wrap}>
      <div style={s.head}>
        <div>
          <div style={s.title}>Year over year</div>
          <div style={s.sub}>
            Each month against the same month a year earlier. Always monthly — pay periods do not
            line up across years.
            {ccLabel && <> Showing <strong style={s.scope}>{ccLabel}</strong> only.</>}
          </div>
        </div>
        <div style={s.controls}>
          {/* Narrowing the WHOLE comparison to one yard, which is a different
              question from breaking the month out BY cost center below. One
              asks "how is Riverside doing against last year", the other "which
              yards moved". Both are asked, so both are offered. */}
          <select id="yoy-cc" aria-label="Cost center"
                  style={s.ccSelect} value={cc} onChange={(e) => setCc(e.target.value)}>
            <option value="">All cost centers</option>
            {(dims.data?.cost_centers || []).map((x) => (
              <option key={x.id} value={x.id}>{x.code} · {x.name}</option>
            ))}
          </select>
          <Segmented options={MEASURE_TABS} value={measure} onChange={setMeasure} />
          <Segmented options={WINDOWS} value={months} onChange={setMonths} />
        </div>
      </div>

      {noneYet && (
        <div style={s.blocker}>
          <strong>No prior-year payroll is loaded yet.</strong> Every month below is drawn without a
          comparison. Once the earlier year is uploaded this panel answers year over year with no
          further change.
        </div>
      )}

      <YoyTrend points={points} measure={measure}
                isLoading={trend.isLoading} error={trend.error} />

      {/* The chart's own points, handed down rather than fetched again -- see
          the header of YoySourceData for why that is the whole filter story. */}
      <YoySourceData points={points} measure={measure} scopeLabel={ccLabel} />

      <div style={s.tableControls}>
        <Field label="Break down by">
          <select id="yoy-by" style={s.select} value={effectiveBy}
                  onChange={(e) => setBy(e.target.value)}>
            {availableDims.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Month">
          <select style={s.select} value={activeMonth} onChange={(e) => setMonth(e.target.value)}>
            {points.filter((p) => p.loaded).slice().reverse().map((p) => (
              <option key={p.month} value={p.month}>
                {p.label}{p.partial ? ' — still running' : ''}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <YoyBreakdown data={breakdown.data} isLoading={breakdown.isLoading}
                    error={breakdown.error}
                    measure={measure === 'hours' ? 'hours' : 'amount'} />
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div style={s.field}>
      <div style={s.fieldLabel}>{label}</div>
      {children}
    </div>
  );
}

function Segmented({ options, value, onChange }) {
  return (
    <div style={s.seg}>
      {options.map((o) => (
        <button key={o.key} type="button" onClick={() => onChange(o.key)}
                style={{ ...s.segBtn, ...(o.key === value ? s.segBtnOn : null) }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

const s = {
  wrap:  { display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '6px' },

  head:  { display: 'flex', flexWrap: 'wrap', gap: '12px',
           alignItems: 'flex-end', justifyContent: 'space-between' },
  title: { ...t.h2, color: c.text },
  sub:   { ...t.caption, color: c.textMuted, marginTop: '2px', maxWidth: '62ch' },

  controls: { display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' },

  blocker: { ...t.bodySm, color: c.warningText, background: c.warningTint,
             border: `1px solid ${c.border}`, borderRadius: '8px', padding: '10px 13px' },

  tableControls: { display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' },
  field:         { display: 'flex', flexDirection: 'column', gap: '5px' },
  fieldLabel:    { ...t.label, color: c.textFaint },

  seg:      { display: 'inline-flex', background: c.surfaceSunk, border: `1px solid ${c.border}`,
              borderRadius: '7px', padding: '2px', gap: '2px' },
  segBtn:   { ...t.bodySm, border: 'none', background: 'transparent', color: c.textMuted,
              padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', whiteSpace: 'nowrap' },
  segBtnOn: { background: c.surface, color: c.text, fontWeight: 600,
              boxShadow: '0 1px 2px rgba(17,24,39,0.08)' },

  ccSelect: { ...t.bodySm, color: c.text, background: c.surface,
              border: `1px solid ${c.borderStrong}`, borderRadius: '7px',
              padding: '5px 9px', maxWidth: '230px', cursor: 'pointer' },
  scope:    { color: c.text },

  select: { ...t.bodySm, color: c.text, background: c.surface, border: `1px solid ${c.borderStrong}`,
            borderRadius: '7px', padding: '6px 10px', minWidth: '190px', cursor: 'pointer' },
};
